<template>
  <v-container
    v-if="authenticationMixin.checkedLogin"
    class="content pa-0"
    fluid
  >
    <!-- Hero section -->
    <page-hero
      title="Uitvaart"
      intro="U kan hier de online condoleances lezen, aanwezigheden op de koffietafel raadplegen en eventueel de uitvaart herbekijken. We begeleiden u graag stap voor stap bij het samenstellen van een unieke uitvaart."
    />

    <!-- Three columns -->
    <v-row
      justify="center"
      class="mb-8 pb-8 uitvaart-columns"
    >
      <v-col
        v-for="(section, sIndex) in sections"
        class="center px-4"
        cols="12"
        md="4"
        :key="sIndex"
      >
        <div class="uitvaart-column">
          <div
            class="uitvaart-column-header"
            :class="'uitvaart-column-header--' + section.theme"
          >
            <div
              class="uitvaart-column-icon"
              :class="'uitvaart-column-icon--' + section.theme"
            >
              <v-icon
                color="white"
                size="22"
              >
                {{ section.icon }}
              </v-icon>
            </div>
            <h2
              class="uitvaart-column-title"
              :class="'uitvaart-column-title--' + section.theme"
            >
              {{ section.title }}
            </h2>
            <p class="uitvaart-column-description">
              {{ section.description }}
            </p>
          </div>
          <div class="uitvaart-column-items">
            <component
              v-for="(item, index) in section.items.filter(i => i.title)"
              :is="item.to ? 'router-link' : 'div'"
              :class="['uitvaart-item', { 'uitvaart-item--disabled': !item.to }]"
              :key="sIndex + '-' + index"
              :to="item.to || undefined"
            >
              <v-icon
                class="uitvaart-item-icon"
                :color="section.itemColor"
                size="22"
              >
                {{ item.icon }}
              </v-icon>
              <div class="uitvaart-item-text">
                <span class="uitvaart-item-title">{{ item.title }}</span>
                <span
                  v-if="item.subtitle"
                  class="uitvaart-item-subtitle"
                >
                  {{ item.subtitle }}
                </span>
                <span
                  v-if="!item.to"
                  class="uitvaart-item-subtitle uitvaart-item-subtitle--italic"
                >
                  Binnenkort beschikbaar
                </span>
              </div>
              <v-icon
                v-if="item.to"
                class="uitvaart-item-arrow"
                color="#bbb"
                size="18"
              >
                mdi-chevron-right
              </v-icon>
            </component>
          </div>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import authenticationMixin from '@/mixins/authenticationMixin'
import PageHero from '@/components/PageHero.vue'

export default {
  name: 'UitvaartComponent',
  components: {
    PageHero
  },
  mixins: [
    authenticationMixin
  ],
  data () {
    return {
      sections: [
        {
          title: 'Raadplegen',
          description: 'Ontvangen berichten en informatie bekijken',
          icon: 'mdi-magnify',
          theme: 'secondary',
          itemColor: '#424E58',
          items: [
            {
              icon: 'mdi-message-text-outline',
              title: 'Condoleances',
              subtitle: 'Online condoleances lezen',
              to: 'Condoleances'
            },
            {
              icon: 'mdi-account-group-outline',
              title: 'Aanwezigen koffietafel',
              subtitle: 'Aanwezigheden raadplegen',
              to: 'Koffietafel'
            },
            {
              icon: 'mdi-file-document-outline',
              title: 'Kostenoverzicht',
              subtitle: 'Overzicht van de kosten',
              to: 'Facturatie'
            }
          ]
        },
        {
          title: 'Opbouw plechtigheid',
          description: 'Stap voor stap de plechtigheid vormgeven',
          icon: 'mdi-book-open-page-variant',
          theme: 'gold',
          itemColor: '#907322',
          items: [
            {
              icon: 'mdi-microphone-outline',
              title: 'Aanspreking',
              to: 'Aanspreking'
            },
            {
              icon: 'mdi-church',
              title: 'Aard van de plechtigheid',
              to: 'AardVanDePlechtigheid'
            },
            {
              icon: 'mdi-star-four-points-outline',
              title: 'Symboliek',
              to: 'Symboliek'
            },
            {
              icon: 'mdi-music-note',
              title: 'Muziek',
              to: 'Muziek'
            },
            {
              icon: 'mdi-camera-outline',
              title: "Foto's",
              to: 'Fotos'
            },
            {
              icon: 'mdi-text-box-outline',
              title: 'Eigen teksten / getuigenissen',
              to: 'Teksten'
            },
            {
              icon: 'mdi-tag-text-outline',
              title: 'Kernwoorden',
              to: 'Kernwoorden'
            },
            {
              icon: 'mdi-book-open-outline',
              title: 'Levensverhaal',
              to: 'Levensverhaal'
            },
            {
              icon: 'mdi-calendar-check-outline',
              title: 'Praktische afspraken',
              to: 'PraktischeAfspraken'
            },
            {
              icon: 'mdi-comment-text-outline',
              title: 'Extra / opmerkingen',
              to: 'ExtraOpmerkingen'
            }
          ]
        },
        {
          title: 'Keuzes uitvaart',
          description: 'Persoonlijke keuzes maken voor de uitvaart',
          icon: 'mdi-heart-outline',
          theme: 'secondary',
          itemColor: '#424E58',
          items: [
            {
              icon: 'mdi-newspaper-variant-outline',
              title: 'Rouwdrukwerk',
              to: 'Rouwdrukwerk'
            },
            {
              icon: 'mdi-flower-tulip-outline',
              title: 'Bloemen',
              to: 'Bloemen'
            },
            {
              icon: 'mdi-bullhorn-outline',
              title: 'Publicaties',
              to: 'Publicaties'
            },
            {
              icon: 'mdi-decagram-outline',
              title: 'Urne',
              to: 'Urne'
            },
            {
              icon: 'mdi-seed-outline',
              title: 'Mini-urne',
              to: 'MiniUrne'
            },
            {
              icon: 'mdi-grave-stone',
              title: 'Grafmonumenten',
              to: 'Grafmonumenten'
            },
            {
              icon: 'mdi-diamond-stone',
              title: 'Herinneringsjuwelen',
              to: 'Herinneringsjuwelen'
            }
          ]
        }
      ]
    }
  },
  mounted () {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    this.AuthenticationMixin_CheckIfLoggedIn()
  }
}
</script>

<style scoped>
/* Theme colors as variables */
.uitvaart-columns {
  --color-secondary: #424E58;
  --color-gold: #907322;
  --color-gold-light: #DEC067;
  --color-cream: #FBF7EE;
  --color-border: #e8e4dc;
  --color-muted: #999;
}

/* Column wrapper */
.uitvaart-columns {
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
}

/* Column layout */
.uitvaart-column {
  height: 100%;
  position: relative;
  padding-left: 40px;
}

.uitvaart-column::before {
  content: '';
  position: absolute;
  left: 16px;
  top: 34px;
  bottom: 0;
  width: 2px;
  background-color: var(--color-border, #e8e4dc);
}

.uitvaart-column-header {
  padding-bottom: 20px;
  margin-bottom: 8px;
  border-bottom: 2px solid;
}

.uitvaart-column-header--secondary {
  border-color: var(--color-secondary, #424E58);
}

.uitvaart-column-header--gold {
  border-color: var(--color-gold-light, #DEC067);
}

.uitvaart-column-icon {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  left: 0;
  top: 0;
  z-index: 1;
}

.uitvaart-column-icon--secondary {
  background-color: var(--color-secondary, #424E58);
}

.uitvaart-column-icon--gold {
  background-color: var(--color-gold, #907322);
}

.uitvaart-column-title {
  font-size: 22px;
  font-weight: 500;
  margin: 0;
  padding: 0;
  line-height: 1.3;
}

.uitvaart-column-title--secondary {
  color: var(--color-secondary, #424E58);
}

.uitvaart-column-title--gold {
  color: var(--color-gold, #907322);
}

.uitvaart-column-description {
  font-size: 15px;
  color: var(--color-muted, #999);
  margin-top: 4px;
  padding: 0;
}

/* Item list */
.uitvaart-column-items {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.uitvaart-item {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-radius: 8px;
  text-decoration: none;
  color: inherit;
  transition: background-color 0.2s ease;
  cursor: pointer;
}

.uitvaart-item:hover {
  background-color: var(--color-cream, #FBF7EE);
}

.uitvaart-item--disabled {
  opacity: 0.45;
  cursor: default;
}

.uitvaart-item--disabled:hover {
  background-color: transparent;
}

.uitvaart-item-icon {
  flex-shrink: 0;
  margin-right: 12px;
}

.uitvaart-item-text {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.uitvaart-item-title {
  font-size: 16px;
  font-weight: 500;
  color: var(--color-secondary, #424E58);
  line-height: 1.3;
}

.uitvaart-item-subtitle {
  font-size: 14px;
  color: var(--color-muted, #999);
  line-height: 1.3;
  margin-top: 1px;
}

.uitvaart-item-subtitle--italic {
  font-style: italic;
}

.uitvaart-item-arrow {
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.uitvaart-item:hover .uitvaart-item-arrow {
  opacity: 1;
}
</style>
